import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, FileText, XCircle, CheckCircle, Truck } from 'lucide-react';
import { OrderDetailModal, InputModal } from '../../../core/components/dashboard/Common';
import { getPrescriptionService } from '../../../domains/prescriptions';
import {
  ORDER_STATUS,
  STATUS_LABELS,
  STATUS_COLORS,
} from '../../../core/constants/orderStatus';
import styles from './OrderList.module.css';

const STATUS_TABS = [
  { key: 'all', label: '전체' },
  { key: ORDER_STATUS.PENDING, label: '승인대기' },
  { key: ORDER_STATUS.APPROVED, label: '승인됨' },
  { key: ORDER_STATUS.PAID, label: '결제완료' },
  { key: ORDER_STATUS.PREPARING, label: '조제중' },
  { key: ORDER_STATUS.COMPLETED, label: '조제완료' },
  { key: ORDER_STATUS.PICKED_UP, label: '픽업완료' },
  { key: ORDER_STATUS.REJECTED, label: '거절됨' },
];

const ITEMS_PER_PAGE = 10;

/**
 * 주문 관리 화면
 */
const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputModal, setInputModal] = useState(null);

  // 필터 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 10,
    totalPages: 0,
  });

  // Tab indicator
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef({});

  // 주문 목록 조회
  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const prescriptionService = getPrescriptionService();

      const params = {
        page: currentPage,
        size: ITEMS_PER_PAGE,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await prescriptionService.getOrderList(params);

      if (response.result_code === '200' && response.data) {
        let items = response.data.items || [];

        if (searchTerm) {
          items = items.filter(
            order =>
              order.user?.name?.includes(searchTerm) ||
              order.user?.phone_number?.includes(searchTerm) ||
              String(order.order_id).includes(searchTerm)
          );
        }

        setOrders(items);
        setPagination({
          total: response.data.total_count || 0,
          page: response.data.page || 1,
          size: response.data.size || 10,
          totalPages: Math.ceil((response.data.total_count || 0) / (response.data.size || 10)),
        });
      }
    } catch (error) {
      console.error('주문 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Tab sliding indicator
  useEffect(() => {
    const tabEl = tabRefs.current[statusFilter];
    if (tabEl) {
      setIndicatorStyle({
        width: `${tabEl.offsetWidth}px`,
        transform: `translateX(${tabEl.offsetLeft}px)`,
      });
    }
  }, [statusFilter]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);

  // KPI 계산
  const kpi = useMemo(() => {
    const counts = {
      total: pagination.total,
      pending: 0,
      paid: 0,
      preparing: 0,
      pickedUp: 0,
    };

    orders.forEach(order => {
      switch (order.status) {
        case ORDER_STATUS.PENDING:
          counts.pending++;
          break;
        case ORDER_STATUS.PAID:
          counts.paid++;
          break;
        case ORDER_STATUS.PREPARING:
          counts.preparing++;
          break;
        case ORDER_STATUS.PICKED_UP:
          counts.pickedUp++;
          break;
        default:
          break;
      }
    });

    return counts;
  }, [orders, pagination.total]);

  const handleOrderClick = order => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const handleOrderUpdate = updatedOrder => {
    fetchOrders();
    setSelectedOrder(updatedOrder);
  };

  // 상태 변경 실행
  const executeStatusChange = async (orderId, newStatus, additionalData = {}) => {
    try {
      const prescriptionService = getPrescriptionService();
      const data = { status: newStatus, ...additionalData };
      await prescriptionService.updateOrderStatus(orderId, data);
      fetchOrders();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = (e, orderId, newStatus) => {
    e.stopPropagation();

    if (newStatus === ORDER_STATUS.APPROVED) {
      setInputModal({
        title: '결제 금액 입력',
        label: '결제 금액',
        placeholder: '금액을 입력하세요',
        type: 'number',
        confirmText: '승인',
        confirmColor: '#2196F3',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(orderId, newStatus, { total_amount: value });
        },
      });
      return;
    }

    if (newStatus === ORDER_STATUS.REJECTED) {
      setInputModal({
        title: '거절 사유 입력',
        label: '거절 사유',
        placeholder: '거절 사유를 입력하세요',
        type: 'text',
        confirmText: '거절',
        confirmColor: '#F44336',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(orderId, newStatus, { rejection_reason: value });
        },
      });
      return;
    }

    executeStatusChange(orderId, newStatus);
  };

  // Pagination
  const totalPages = Math.max(1, pagination.totalPages);

  return (
      <div className={styles.pageContainer}>
        {/* KPI Row */}
        <div className={styles.creditInfoRow}>
          <div className={styles.creditBox}>
            <div className={styles.moneyRow}>
              <div className={styles.totalMoney}>
                <div className={styles.boxLabel}>총 주문</div>
                <div className={styles.boxTotalValue}>{kpi.total}건</div>
              </div>
              <div className={styles.verticalDivider}></div>
              <div className={styles.moneyDetails}>
                <div className={styles.moneyTypeRow}>
                  <span className={styles.boxLabel}>승인대기</span>
                  <span className={styles.boxValue}>{kpi.pending}건</span>
                </div>
                <div className={styles.moneyTypeRow}>
                  <span className={styles.boxLabel}>결제완료</span>
                  <span className={styles.boxValue}>{kpi.paid}건</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.creditBox}>
            <div className={styles.boxLabel}>조제중</div>
            <div className={styles.boxTotalValue}>{kpi.preparing}건</div>
          </div>
          <div className={styles.creditBox}>
            <div className={styles.boxLabel}>픽업완료</div>
            <div className={styles.boxTotalValue}>{kpi.pickedUp}건</div>
          </div>
        </div>

        {/* History Section */}
        <div className={styles.historySection}>
          {/* 상태별 탭 */}
          <div className={styles.tabs}>
            <div className={styles.tabIndicator} style={indicatorStyle}></div>
            {STATUS_TABS.map(tab => (
              <button
                key={tab.key}
                ref={el => (tabRefs.current[tab.key] = el)}
                className={`${styles.tab} ${statusFilter === tab.key ? styles.activeTab : ''}`}
                onClick={() => setStatusFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search + Filter Bar */}
          <div className={styles.searchFilterRow}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="환자명, 전화번호, 주문번호 검색..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.filterBar}>
              <span className={styles.resultCount}>
                총 {pagination.total}건 {isLoading && '(로딩중...)'}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>주문번호</th>
                  <th>상태</th>
                  <th>환자명</th>
                  <th>연락처</th>
                  <th>금액</th>
                  <th>주문시간</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className={styles.emptyState}>
                      <div className={styles.emptyText}>로딩 중...</div>
                    </td>
                  </tr>
                ) : orders.length > 0 ? (
                  orders.map(order => (
                    <tr
                      key={order.order_id}
                      className={styles.clickableRow}
                      onClick={() => handleOrderClick(order)}
                    >
                      <td className={styles.orderId}>#{order.order_id}</td>
                      <td>
                        <span
                          className={styles.statusBadge}
                          style={{
                            backgroundColor: `${STATUS_COLORS[order.status]}20`,
                            color: STATUS_COLORS[order.status],
                          }}
                        >
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </td>
                      <td>{order.user?.name || '-'}</td>
                      <td>{order.user?.phone_number || '-'}</td>
                      <td className={styles.amount}>
                        {order.total_amount
                          ? `${order.total_amount.toLocaleString()}원`
                          : '-'}
                      </td>
                      <td className={styles.time}>
                        {new Date(order.created_at).toLocaleString('ko-KR', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          {order.status === ORDER_STATUS.PENDING && (
                            <>
                              <button
                                className={`${styles.actionBtn} ${styles.preparingBtn}`}
                                onClick={e => handleStatusChange(e, order.order_id, ORDER_STATUS.APPROVED)}
                                title="승인"
                              >
                                <CheckCircle size={18} />
                                승인
                              </button>
                              <button
                                className={`${styles.actionBtn} ${styles.declineBtn}`}
                                onClick={e => handleStatusChange(e, order.order_id, ORDER_STATUS.REJECTED)}
                                title="거절"
                              >
                                <XCircle size={18} />
                                거절
                              </button>
                            </>
                          )}
                          {order.status === ORDER_STATUS.APPROVED && (
                            <span className={styles.waitingText}>결제 대기중</span>
                          )}
                          {order.status === ORDER_STATUS.PAID && (
                            <button
                              className={`${styles.actionBtn} ${styles.preparingBtn}`}
                              onClick={e => handleStatusChange(e, order.order_id, ORDER_STATUS.PREPARING)}
                              title="조제 시작"
                            >
                              <CheckCircle size={18} />
                              조제시작
                            </button>
                          )}
                          {order.status === ORDER_STATUS.PREPARING && (
                            <button
                              className={`${styles.actionBtn} ${styles.readyBtn}`}
                              onClick={e => handleStatusChange(e, order.order_id, ORDER_STATUS.COMPLETED)}
                              title="조제 완료"
                            >
                              <CheckCircle size={18} />
                              조제완료
                            </button>
                          )}
                          {order.status === ORDER_STATUS.COMPLETED && (
                            <button
                              className={`${styles.actionBtn} ${styles.completeBtn}`}
                              onClick={e => handleStatusChange(e, order.order_id, ORDER_STATUS.PICKED_UP)}
                              title="픽업 완료"
                            >
                              <Truck size={18} />
                              픽업완료
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className={styles.emptyState}>
                      <FileText className={styles.emptyIcon} />
                      <div className={styles.emptyText}>검색 결과가 없습니다</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className={styles.paginationBar}>
              <div className={styles.paginationInfo}>
                {ITEMS_PER_PAGE}개씩 결과
              </div>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  ‹
                </button>
                <button className={`${styles.pageBtn} ${styles.activePage}`}>
                  {currentPage}
                </button>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={handleCloseModal}
            onUpdate={handleOrderUpdate}
          />
        )}

        {inputModal && (
          <InputModal
            title={inputModal.title}
            label={inputModal.label}
            placeholder={inputModal.placeholder}
            type={inputModal.type}
            confirmText={inputModal.confirmText}
            confirmColor={inputModal.confirmColor}
            onConfirm={inputModal.onConfirm}
            onCancel={() => setInputModal(null)}
          />
        )}
      </div>
  );
};

export default OrderList;
